import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

/**
 * Full day/night cycle length. 10 real-time minutes is long enough to feel like a real cycle but
 * short enough that players see both states in a normal session.
 */
const CYCLE_MS = 5 * 60 * 1000

/**
 * Smoothstep over `[edge0, edge1]` — used to flatten the "deep night" / "deep day" plateaus so
 * most of the cycle reads as one state, with a deliberate transition between.
 */
function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)))
  return t * t * (3 - 2 * t)
}

export type DayNightPhase = 'day' | 'sunset' | 'night' | 'sunrise'

export interface DayNightState {
  /** Raw cycle position. `0` = midnight, `0.25` = sunrise, `0.5` = noon, `0.75` = sunset. */
  progress: number
  /** 0 (full day) → 1 (full night) with flattened plateaus. Drives overlay opacities. */
  darkness: number
  /** Coarse phase tag for any logic gating (e.g. "skip ambient flag wave at night"). */
  phase: DayNightPhase
}

const DayNightContext = createContext<DayNightState>({
  progress: 0.5,
  darkness: 0,
  phase: 'day',
})

export function useDayNight(): DayNightState {
  return useContext(DayNightContext)
}

function computeState(nowMs: number): DayNightState {
  const progress = (nowMs % CYCLE_MS) / CYCLE_MS
  /** `cos(progress * 2π)` peaks at midnight (progress=0) and dips at noon (progress=0.5). */
  const nightIntensity = 0.5 + 0.5 * Math.cos(progress * Math.PI * 2)
  /**
   * Narrow plateaus + wide transition window: only the very deepest day / night sit at a
   * constant value; most of the cycle is in a smooth gradient between the two states.
   */
  const darkness = smoothstep(0.05, 0.95, nightIntensity)
  let phase: DayNightPhase
  if (darkness < 0.1) phase = 'day'
  else if (darkness > 0.9) phase = 'night'
  else if (progress < 0.5) phase = 'sunrise'
  else phase = 'sunset'
  return { progress, darkness, phase }
}

/**
 * Provider — ticks 4× / sec. Sufficient resolution for smooth overlay-opacity interpolation while
 * staying off the render hot path; consumers see one stable object per tick.
 */
export function DayNightProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<DayNightState>(() => computeState(Date.now()))

  useEffect(() => {
    const id = window.setInterval(() => {
      setState(computeState(Date.now()))
    }, 250)
    return () => window.clearInterval(id)
  }, [])

  const value = useMemo(() => state, [state])
  return <DayNightContext.Provider value={value}>{children}</DayNightContext.Provider>
}
